import { generatePath, useNavigate } from 'react-router';

import { PATHS, PATIENT_PROFILE_SCROLL_KEY_PREFIX } from '@/shared/config';
import { useAppSelector, useUserTimezone } from '@/shared/hooks';
import { safeSessionStorage, trackEvent } from '@/shared/lib';

import { composedModel } from '@/entities/composed';
import {
  getConsultationPath,
  type TranscriptorConsultation,
  useStaleConsultationDetection,
  useTranscriptorConsultations,
} from '@/entities/transcriptor';

import { getRecordingsPreview } from '../lib';

export const usePatientRecordingsCard = (patientID: string) => {
  const navigate = useNavigate();
  const userTimezone = useUserTimezone();

  const canViewArchive = useAppSelector(
    composedModel.selectors.selectCanViewPatientRecordingsArchive(patientID),
  );

  const { consultations, hiddenConsultations, visibleTotal, isLoaded } =
    useTranscriptorConsultations(patientID, {
      includeHidden: canViewArchive,
    });
  const { isConsultationStale, forceStop } = useStaleConsultationDetection();

  const previewConsultations = getRecordingsPreview(consultations);

  const handleCardClick = () => {
    trackEvent({
      eventName: 'PatientProfilePageRecordingsWidgetClicked',
      properties: { patient_id: patientID },
    });

    navigate(generatePath(PATHS.patientRecordings, { patientID }));
  };

  const handleItemClick = (consultation: TranscriptorConsultation) => {
    if (consultation.status === 'created') {
      return;
    }

    safeSessionStorage.setItem(
      `${PATIENT_PROFILE_SCROLL_KEY_PREFIX}${patientID}`,
      String(window.scrollY),
    );
    safeSessionStorage.setItem('lastScrollTime', String(Date.now()));

    trackEvent({
      eventName: 'PatientProfilePageRecordingsWidgetItemClicked',
      properties: { patient_id: patientID },
    });

    navigate(
      getConsultationPath({ patientID, consultationID: consultation.id }),
    );
  };

  return {
    previewConsultations,
    visibleTotal,
    isVisible: isLoaded && (visibleTotal > 0 || hiddenConsultations.length > 0),
    userTimezone,
    isConsultationStale,
    forceStop,
    handleCardClick,
    handleItemClick,
  };
};
